import { memo, useState, useEffect, useContext, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { Button, Modal, ModalBody } from "react-bootstrap";
import MainService from "../Services/MainService";
import { MdCancel, MdOutlineTimerOff } from "react-icons/md";
import { IoCheckmarkCircle } from "react-icons/io5";
import { IoMdWallet } from "react-icons/io";
import PaymentDataContext from "../PaymentDataContext";
import { PulseLoader } from "react-spinners";
import { useDispatch, useSelector } from "react-redux";
import AlertService from "../Services/AlertService";
import ApiService from "../Services/ApiService";
import { setTronPaymentRequestAgain, setWalletconnectPaymentRequestAgain } from "../Store/Reducers/mainReducer";
import { TRON_PLATFORM_TYPE } from "../Constants/MainKeys";
import { redirectToMerchant } from "../utils/paymentVisitStorage";


const getScanUrl = (paymentData) => {
    if (!paymentData || !paymentData?.cryptoTransactionHash || !paymentData?.cryptoNetworkId) return null;
    switch (paymentData.cryptoNetworkId) {
        case 1:
            return <a href={`https://etherscan.io/tx/${paymentData.cryptoTransactionHash}`} target="_blank" rel="noopener noreferrer">
                View TXN on EtherScan
            </a>
        case 5:
            return <a href={`https://bscscan.com/tx/${paymentData.cryptoTransactionHash}`} target="_blank" rel="noopener noreferrer">
                View TXN on BscScan
            </a>
        case 10:
            return <a href={`https://tronscan.org/#/transaction/${paymentData.cryptoTransactionHash}`} target="_blank" rel="noopener noreferrer">
                View TXN on TronScan
            </a>
        case 1013:
            return <a href={`https://arbiscan.io/tx/${paymentData.cryptoTransactionHash}`} target="_blank" rel="noopener noreferrer">
                View TXN on ArbiScan
            </a>
        default:
            return <a href={`https://blockscan.com/tx/${paymentData.cryptoTransactionHash}`} target="_blank" rel="noopener noreferrer">
                View TXN on BlockScan
            </a>
    }
}

// A payment request has been sent to your wallet
const TxPart0 = memo(({ paymentData, cancelPayment, selectedChain }) => {

    if (paymentData.status !== 0) return null;

    const paymentTime = paymentData?.paymentRequestedDatetime;
    if (paymentTime === "0001-01-01T00:00:00") { return null; }
    const ptimeNow = MainService.convertUTCDateToLocalDate(new Date(paymentTime));
    const initialSeconds = Math.max(30 * 60 - (Date.now() / 1000 - ptimeNow.getTime() / 1000), 0);

    const [timeRemaining, setTimeRemaining] = useState(initialSeconds);
    const allowUnloadRef = useRef(false);

    const dispatch = useDispatch();

    useEffect(() => {
        const message =
            "A payment request has been sent to your wallet. If you reload this page, click \"Request payment again\" after reload — the wallet session changes after auto-reconnect.";

        const handleBeforeUnload = (e) => {
            if (allowUnloadRef.current) return;
            e.preventDefault();
            e.returnValue = message;
            return message;
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, []);

    useEffect(() => {
        const message =
            "A payment request has been sent to your wallet.\n\nPlease wait and accept it in your wallet.\n\nIf you reload this page, click \"Request payment again\" after reload — the wallet session changes after auto-reconnect.";

        const handleKeyDown = (e) => {
            const isF5 = e.key === "F5" || e.keyCode === 116;
            const isReloadCombo =
                (e.ctrlKey || e.metaKey) && (e.key?.toLowerCase?.() === "r" || e.keyCode === 82);

            if (!isF5 && !isReloadCombo) return;

            e.preventDefault();
            e.stopPropagation();

            const ok = window.confirm(message);
            if (!ok) return;

            allowUnloadRef.current = true;
            window.location.reload();
        };

        window.addEventListener("keydown", handleKeyDown, { capture: true });
        return () => window.removeEventListener("keydown", handleKeyDown, { capture: true });
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setTimeRemaining(prev => Math.max(prev - 1, 0));
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        // сбрасываем, если пришёл новый paymentTime
        // 5*60 (5 min) 
        const newSeconds = Math.max(30 * 60 - (Date.now() / 1000 - ptimeNow.getTime() / 1000), 0);
        setTimeRemaining(newSeconds);
    }, [paymentTime]);

    useEffect(() => {
        if (Math.round(timeRemaining) === 0) {
            cancelPayment(false);
        }
    }, [timeRemaining]);

    const requestPaymentAgain = (selectedChain) => {
        AlertService.alertConfirm(
            `Are you sure ?`,
            "By continuing, you accept full responsibility for this operation and any outcomes associated with it. No liability is assumed on our side.",
            "Yes",
            "No"
        ).then(() => {
            if (selectedChain.platformType === TRON_PLATFORM_TYPE) {
                dispatch(setTronPaymentRequestAgain(true))
            } else {
                dispatch(setWalletconnectPaymentRequestAgain(true))
            }
        }).catch(() => { });
    }

    return (
        <div className="cx-modal-body-wrapper txPart">
            <p><strong className="cx-d-block cx-spiner-text">A payment request has been sent to your wallet</strong></p>
            <div className="mb-2">
                <IoMdWallet color="#0533B3" size={60} />
            </div>
            <p className="cx-d-block mb-0">Please accept within</p>
            <strong className="cx-d-block cx-blue-color mb-2 d-block">
                {MainService.secondsToString(Math.floor(timeRemaining))}
            </strong>
            <div className="p-2" style={{ backgroundColor: "#cff4fc", borderRadius: "12px" }}>
                <small className="d-block">*If not accepted in time, the transaction will be canceled.</small>
            </div>
            <div className="d-flex gap-2">
                <Button
                    className="w-100"
                    variant="danger"
                    onClick={() => cancelPayment(true)}
                >
                    Cancel Payment
                </Button>
                {
                    selectedChain ?
                        <Button
                            className="w-100"
                            variant="primary"
                            onClick={() => {
                                requestPaymentAgain(selectedChain);
                            }}
                        >
                            Request payment again
                        </Button>
                        : null
                }
            </div>
        </div>
    );
});

// Waiting for TXN
const TxPart1 = memo(({ paymentData }) => {
    if (paymentData.status !== 1) return null;
    return (
        <div>
            <div className="cx-modal-body-wrapper txPart">
                <div className="d-flex align-items-end justify-content-center">
                    <strong className="cx-d-block cx-spiner-text">Waiting for TXN</strong>
                    <PulseLoader style={{ marginBottom: "-1px" }} size={3} speedMultiplier={0.5} />
                </div>
                <div className="cx-loader-wrapper">
                    <div className="loader spiner-20"></div>
                </div>
                {getScanUrl(paymentData)}
            </div>
        </div>
    );
});

// Sorry, your transaction is failed!
const TxPart2 = memo(({ paymentData }) => (
    <div className="cx-modal-body-wrapper txPart">
        <p><strong className="cx-d-block">Sorry, your transaction is failed!</strong></p>
        <div className="my-2">
            <MdCancel color="#B30505" size={60} />
        </div>
        {
            paymentData?.errorCode || paymentData?.errorMessage ?
                <div className="text-danger d-flex flex-column gap-2" style={{ lineHeight: "15px", margin: "15px 0", wordBreak: "break-word" }} >
                    <small>{paymentData?.errorCode}</small>
                    <small>{paymentData?.errorMessage}</small>
                </div>
                : null
        }
        <Link to="#" className="my-2 d-block">
            Please try again
        </Link>
    </div>
));

// You cancelled or rejected the current transaction!
const TxPart3 = memo(({ paymentData, onBackToMerchant }) => {
    if (paymentData.status !== 3) return null;
    return (
        <div className="cx-modal-body-wrapper txPart">
            <p><strong className="cx-d-block">You cancelled or rejected the current transaction!</strong></p>
            <div className="mb-2">
                <MdCancel color="#B30505" size={60} />
            </div>
            {
                paymentData?.errorCode || paymentData?.errorMessage ?
                    <div className="text-danger d-flex flex-column gap-2" style={{ lineHeight: "15px", margin: "15px 0", wordBreak: "break-word" }} >
                        <small>{paymentData?.errorCode}</small>
                        <small>{paymentData?.errorMessage}</small>
                    </div>
                    : null
            }
            {
                paymentData.cancelUrl ?
                    <Link to="#" onClick={() => onBackToMerchant?.(paymentData.cancelUrl)} className="my-2 d-block">
                        Back to merchant
                    </Link>
                    : null
            }
        </div>
    );
});

// Sorry, your transaction is rejected!
const TxPart4 = memo(({ paymentData }) => (
    <div className="cx-modal-body-wrapper txPart">
        <p><strong className="cx-d-block">Sorry, your transaction is rejected!</strong></p>
        <div className="my-2">
            <MdCancel color="#B30505" size={60} />
        </div>
        {
            paymentData?.errorCode || paymentData?.errorMessage ?
                <div className="text-danger d-flex flex-column gap-2" style={{ lineHeight: "15px", margin: "15px 0", wordBreak: "break-word" }} >
                    <small>{paymentData?.errorCode}</small>
                    <small>{paymentData?.errorMessage}</small>
                </div>
                : null
        }
        <Link to="#" className="my-2 d-block">
            Please try again
        </Link>
    </div>
));

// Your Order is Complete!
const TxPart5 = memo(({ paymentData }) => {

    return <div className="cx-modal-body-wrapper txPart">
        <div className="cx-merchant-logo">
            <div id="orgImage"></div>
        </div>
        <div className="cx-modal-body-container">
            <div className="cx-modal-body-wrapper">
                <div className="cx-modal-body">
                    <strong className="cx-window-title">Your Order is Complete!</strong>
                    <div className="cx-modal-main-title">
                        <strong className="orgName">Order: </strong>
                        <span className="orderNum">{paymentData.remoteTransactionId ? paymentData.remoteTransactionId : ""}</span>
                    </div>
                    <div className="my-2"><IoCheckmarkCircle size={60} color="#05B32E" /></div>
                    {getScanUrl(paymentData)}
                    {
                        paymentData?.successUrl ?
                            <p className="cx-d-block cx-info-text">
                                <br />
                                You may now close this window
                                <br />
                                <a href={`${paymentData.successUrl}`} rel="noopener noreferrer">Return to merchant</a>
                            </p>
                            : null
                    }
                </div>
            </div>
        </div>
    </div>
});

// Transaction has been cancelled successfully
const TxPart6 = memo(({ paymentData, onBackToMerchant }) => (
    <div className="cx-modal-body-wrapper txPart">
        <p><strong className="cx-d-block cx-spiner-text">Transaction has been cancelled successfully</strong></p>
        <div className="my-2"><IoCheckmarkCircle size={60} color="#B30505" /></div>
        {
            paymentData.cancelUrl ?
                <Link to="#" onClick={() => onBackToMerchant?.(paymentData.cancelUrl)} className="my-2 d-block">
                    Back to merchant
                </Link>
                : null
        }
    </div>
));

// Sorry, your transaction request has timed out.
const TxPart7 = memo(({ paymentData }) => (
    <div className="cx-modal-body-wrapper txPart">
        <p>
            <strong className="cx-d-block cx-spiner-text">
                Sorry, your transaction request has timed out.
            </strong>
        </p>
        <div className="my-2">
            <MdOutlineTimerOff size={60} color="#B30505" />
        </div>
        {
            paymentData?.errorCode || paymentData?.errorMessage ?
                <div className="text-danger d-flex flex-column gap-2" style={{ lineHeight: "15px", margin: "15px 0", wordBreak: "break-word" }} >
                    <small>{paymentData?.errorCode}</small>
                    <small>{paymentData?.errorMessage}</small>
                </div>
                : null
        }
        <Link to="#" className="my-2 d-block">
            Please try again
        </Link>
    </div>
));


const TransactionWaiting = ({ cancelPayment, getPaymentData, selectedChain }) => {

    const { token } = useParams();
    const { isErrorModalVisible } = useSelector(state => state.error);
    const { paymentData } = useContext(PaymentDataContext);

    const onBackToMerchant = (url) => redirectToMerchant(token, url);

    if (!paymentData || isErrorModalVisible) return null;

    const returnTxPart = () => {
        switch (paymentData.status) {
            case 0:
                return <TxPart0
                    paymentData={paymentData}
                    cancelPayment={cancelPayment}
                    getPaymentData={getPaymentData}
                    selectedChain={selectedChain}
                />
            case 1:
                return <TxPart1 paymentData={paymentData} />
            case 2:
                return <TxPart2 paymentData={paymentData} />
            case 3:
                return <TxPart3 paymentData={paymentData} onBackToMerchant={onBackToMerchant} />
            case 4:
                return <TxPart4 paymentData={paymentData} />
            case 5:
                return <TxPart5 paymentData={paymentData} />
            case 6:
                return <TxPart6 paymentData={paymentData} onBackToMerchant={onBackToMerchant} />
            case 7:
                return <TxPart7 />
            default:
                break;
        }
    }

    const shouldShowModal = (paymentData) => {
        if (!paymentData) return false;
        if ([1, 2, 3, 4, 5, 6, 7].includes(paymentData.status)) {
            return true;
        }
        if (paymentData.status === 0 && paymentData.paymentRequested && paymentData.paymentRequestedDatetime !== "0001-01-01T00:00:00") {
            return true;
        }
        return false;
    };

    return (
        <Modal
            show={shouldShowModal(paymentData)}
            size="md"
            centered
        >
            <ModalBody>
                <div className="cx-modal-container">
                    <div className="cx-modal-wrapper">
                        <div className="cx-modal-body-container">
                            {returnTxPart()}
                        </div>
                    </div>
                </div>
            </ModalBody>
        </Modal>
    );
};

export default TransactionWaiting;
